import { useEffect } from "react";
import type { MeDTO } from "@easyshop/shared";
import { queryClient } from "@/lib/query-client";
import { Button } from "@/components/ui";
import { logout } from "@/layouts/HeaderControls";

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-page px-4">
      <div className="w-full max-w-[440px] rounded-card border border-line bg-white px-6 py-7">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-[40px] w-[40px] items-center justify-center rounded-[11px] bg-sidebar font-display text-[16px] font-extrabold text-white">
            ES
          </div>
          <div className="font-display text-[19px] font-bold">{title}</div>
        </div>
        {children}
        <button type="button" onClick={() => void logout()} className="mt-6 block text-[12.5px] font-semibold text-muted">
          Se déconnecter
        </button>
      </div>
    </div>
  );
}

/**
 * Compte sans accès :
 *  - en attente de validation par un administrateur (inscription libre) ;
 *  - refusé ;
 *  - validé mais rattaché à aucun réseau (attente d'une invitation).
 */
export function OnboardingPage({ me }: { me: MeDTO }) {
  // Vérifie régulièrement si l'administrateur a validé le compte.
  useEffect(() => {
    if (me.approval !== "PENDING") return;
    const t = setInterval(() => void queryClient.invalidateQueries({ queryKey: ["me"] }), 30_000);
    return () => clearInterval(t);
  }, [me.approval]);

  if (me.approval === "REJECTED") {
    return (
      <Frame title="Accès refusé">
        <p className="text-[14px] leading-[1.55] text-ink-2">
          L'administrateur n'a pas validé l'accès de ce compte ({me.user.email}). Si c'est une erreur, contactez votre
          tête de réseau.
        </p>
      </Frame>
    );
  }
  if (me.approval === "PENDING") {
    return (
      <Frame title="Compte en attente de validation">
        <p className="text-[14px] leading-[1.55] text-ink-2">
          Merci {me.user.name}. Votre compte ({me.user.email}) a bien été créé ; un administrateur doit maintenant le
          valider avant que vous puissiez accéder à l'application.
        </p>
        <p className="mt-3 text-[12.5px] text-muted">
          Si vous avez reçu un lien d'invitation, ouvrez-le : il donne accès directement.
        </p>
        <Button className="mt-5" variant="outline" onClick={() => void queryClient.invalidateQueries({ queryKey: ["me"] })}>
          Vérifier à nouveau
        </Button>
      </Frame>
    );
  }
  return (
    <Frame title={`Bienvenue ${me.user.name}`}>
      <p className="text-[14px] leading-[1.55] text-ink-2">
        Votre compte est validé mais n'a encore accès à aucun réseau. Ouvrez le lien d'invitation reçu de votre tête de
        réseau, ou demandez-lui de vous inviter à l'adresse {me.user.email}.
      </p>
    </Frame>
  );
}
